import { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, X, KeyRound, Check, AlertTriangle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Small modal launched from the Auth Gate's "Forgot password?" link.
   Backend returns 200 regardless of whether the email is registered (so we
   don't leak account existence). The boolean response.email_sent decides
   the success copy:
     - email_sent=true                  → "Check your inbox for a temp password"
     - service_configured=false         → "Email service not configured · contact admin"
     - email_sent=false  + configured   → graceful "We weren't able to send right now"
*/
export default function ForgotPasswordModal({ open, defaultEmail = '', onClose }) {
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok, email_sent, service_configured }
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setEmail(defaultEmail);
      setResult(null);
      setErr('');
    }
  }, [open, defaultEmail]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr('');
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setResult(data || { ok: true, email_sent: false, service_configured: false });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // What to render in the success area depends on the response. We keep the
  // language deliberately vague about whether the email exists — every
  // path reads "If your account exists…" so attackers can't enumerate.
  const renderResult = () => {
    if (!result) return null;
    if (!result.service_configured) {
      // Email service not configured — admin needs to manually reset.
      return (
        <div className="rounded-lg p-3 mt-3 flex items-start gap-2" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }} data-testid="forgot-password-no-service">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#92400E' }} />
          <div className="text-[12px]" style={{ color: '#92400E', fontFamily: 'Manrope' }}>
            <p className="font-bold mb-0.5">Email service not configured</p>
            <p>Please contact your administrator at <a href="mailto:andres@heavenlyaisolutions.com" className="underline">andres@heavenlyaisolutions.com</a> to reset your password manually.</p>
          </div>
        </div>
      );
    }
    if (result.email_sent) {
      return (
        <div className="rounded-lg p-3 mt-3 flex items-start gap-2" style={{ backgroundColor: '#DCFCE7', border: '1px solid #86EFAC' }} data-testid="forgot-password-sent">
          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#166534' }} />
          <div className="text-[12px]" style={{ color: '#166534', fontFamily: 'Manrope' }}>
            <p className="font-bold mb-0.5">Check your inbox</p>
            <p>If an account exists for <b>{email}</b>, we just emailed a temporary password. Sign in with it, then change your password immediately from the "Edit profile" pill.</p>
          </div>
        </div>
      );
    }
    // Configured but send failed (network, Resend rejection, etc.).
    return (
      <div className="rounded-lg p-3 mt-3 flex items-start gap-2" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5' }}>
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#B91C1C' }} />
        <div className="text-[12px]" style={{ color: '#B91C1C', fontFamily: 'Manrope' }}>
          <p className="font-bold mb-0.5">We couldn't send the reset email</p>
          <p>Please try again in a moment, or contact your administrator if the issue persists.</p>
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center px-4 py-6"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={onClose}
      data-testid="forgot-password-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ backgroundColor: 'white' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg,#003DA5,#1F4FBE)', color: 'white' }}>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80" style={{ fontFamily: 'Manrope' }}>
              <KeyRound className="w-3 h-3 inline mr-1 -mt-0.5" /> Account recovery
            </p>
            <h3 className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Forgot your password?</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="forgot-password-close"
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-3">
          <p className="text-xs leading-relaxed" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
            Enter the email associated with your account. If we find a match, we'll email you a temporary password you can use to sign in right away.
          </p>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
              <Mail className="w-3 h-3 inline mr-1 -mt-0.5" /> Email address
            </label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@hotel.com"
              data-testid="forgot-password-email"
              className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
              style={{ borderColor: '#A3D4D4', fontFamily: 'Manrope', backgroundColor: '#FAF8F3' }}
              disabled={busy || Boolean(result)}
            />
          </div>

          {err && (
            <div className="rounded-lg px-3 py-2 text-[12px]" style={{ backgroundColor: '#FEF2F2', color: '#B91C1C', fontFamily: 'Manrope' }} data-testid="forgot-password-error">
              {err}
            </div>
          )}

          {renderResult()}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="forgot-password-cancel"
              className="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold"
              style={{ backgroundColor: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}
            >
              {result ? 'Done' : 'Cancel'}
            </button>
            {!result && (
              <button
                type="submit"
                disabled={busy || !email.trim()}
                data-testid="forgot-password-submit"
                className="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold text-white flex items-center justify-center gap-1.5 disabled:opacity-60"
                style={{ backgroundColor: '#1F4FBE', fontFamily: 'Manrope' }}
              >
                {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</> : 'Send reset email'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
